import { Alert, Box, Card, TextField, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { useEffect, useState } from "react";
import LogsService from "services/logs";
import UsersService from "services/users";
import { ptBR_DataGrid } from "utils/datagrid";
import {
  ACOES_LOG,
  AREAS_LOG,
  alteracoesDoLog,
  rotuloCampo,
  valorLog,
} from "utils/logRotulos";

const resumo = (log) => {
  const alteracoes = alteracoesDoLog(log);
  if (alteracoes.length) {
    return alteracoes
      .map((a) =>
        log.action === "edita"
          ? `${rotuloCampo(a.campo)}: ${valorLog(a.campo, a.de)} → ${valorLog(
              a.campo,
              a.para
            )}`
          : `${rotuloCampo(a.campo)}: ${valorLog(a.campo, a.para ?? a.de)}`
      )
      .join(" · ");
  }
  if (log.changedData)
    return "(formato antigo) " + JSON.stringify(log.changedData).slice(0, 300);
  return "";
};

/**
 * Logs (só administrador): quem fez o quê, quando, em qual registro. Os logs
 * novos guardam só o que mudou (de → para); os antigos aparecem em bruto.
 */
export default function Logs() {
  const [filtros, setFiltros] = useState({
    area: "",
    action: "",
    user: "",
    busca: "",
    registro: "",
    dataInicio: "",
    dataFim: "",
  });
  const [pagina, setPagina] = useState({ page: 0, pageSize: 50 });
  const [areas, setAreas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [detalhe, setDetalhe] = useState(null);

  useEffect(() => {
    LogsService.areas()
      .then(setAreas)
      .catch(() => {});
    UsersService.list()
      .then((r) => setUsuarios(r?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      setErro("");
      try {
        const r = await LogsService.listar({
          ...filtros,
          page: pagina.page,
          limit: pagina.pageSize,
        });
        setRows(r.data.map((l) => ({ ...l, id: l._id })));
        setTotal(r.total);
      } catch (e) {
        setErro(e?.error || "Erro ao carregar os logs.");
      }
      setLoading(false);
    }, 300); // espera parar de digitar
    return () => clearTimeout(t);
  }, [filtros, pagina]);

  const alterar = (campo) => (e) => {
    setPagina((p) => ({ ...p, page: 0 }));
    setFiltros((f) => ({ ...f, [campo]: e.target.value }));
  };
  const campo = { size: "small", InputLabelProps: { shrink: true } };
  const select = { ...campo, select: true, SelectProps: { native: true } };

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              Logs do sistema
            </MDTypography>
          </MDBox>
          <MDBox p={3}>
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              <TextField
                label="Área"
                value={filtros.area}
                onChange={alterar("area")}
                sx={{ minWidth: 190 }}
                {...select}
              >
                <option value="">Todas</option>
                {areas.map((a) => (
                  <option key={a} value={a}>
                    {AREAS_LOG[a] || a}
                  </option>
                ))}
              </TextField>
              <TextField
                label="Ação"
                value={filtros.action}
                onChange={alterar("action")}
                sx={{ minWidth: 130 }}
                {...select}
              >
                <option value="">Todas</option>
                {Object.entries(ACOES_LOG).map(([v, r]) => (
                  <option key={v} value={v}>
                    {r}
                  </option>
                ))}
              </TextField>
              {usuarios.length > 0 && (
                <TextField
                  label="Usuário"
                  value={filtros.user}
                  onChange={alterar("user")}
                  sx={{ minWidth: 170 }}
                  {...select}
                >
                  <option value="">Todos</option>
                  {usuarios.map((u) => (
                    <option key={u._id || u.id} value={u._id || u.id}>
                      {u.name || u.attributes?.name || u.email}
                    </option>
                  ))}
                </TextField>
              )}
              <TextField
                label="Busca (descrição/pedido)"
                value={filtros.busca}
                onChange={alterar("busca")}
                {...campo}
              />
              <TextField
                label="Id do registro"
                value={filtros.registro}
                onChange={alterar("registro")}
                {...campo}
              />
              <TextField
                label="De"
                type="date"
                value={filtros.dataInicio}
                onChange={alterar("dataInicio")}
                {...campo}
              />
              <TextField
                label="Até"
                type="date"
                value={filtros.dataFim}
                onChange={alterar("dataFim")}
                {...campo}
              />
            </Box>
            {erro && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {erro}
              </Alert>
            )}
            <DataGrid
              rows={rows}
              autoHeight
              density="compact"
              loading={loading}
              localeText={ptBR_DataGrid}
              rowSelection={false}
              paginationMode="server"
              rowCount={total}
              paginationModel={pagina}
              onPaginationModelChange={setPagina}
              pageSizeOptions={[50, 100, 200]}
              disableColumnFilter
              onRowClick={({ row }) => setDetalhe(row)}
              columns={[
                {
                  field: "createdAt",
                  headerName: "Quando",
                  minWidth: 150,
                  sortable: false,
                  valueGetter: ({ row }) =>
                    new Date(row.createdAt).toLocaleString("pt-BR"),
                },
                {
                  field: "user",
                  headerName: "Usuário",
                  minWidth: 130,
                  sortable: false,
                  valueGetter: ({ row }) =>
                    row.user?.name || row.user?.email || "",
                },
                {
                  field: "area",
                  headerName: "Área",
                  minWidth: 150,
                  sortable: false,
                  valueGetter: ({ row }) => AREAS_LOG[row.area] || row.area,
                },
                {
                  field: "action",
                  headerName: "Ação",
                  minWidth: 90,
                  sortable: false,
                  valueGetter: ({ row }) => ACOES_LOG[row.action] || row.action,
                },
                {
                  field: "rotulo",
                  headerName: "Registro",
                  flex: 0.8,
                  sortable: false,
                },
                {
                  field: "resumo",
                  headerName: "O que mudou",
                  flex: 2,
                  sortable: false,
                  valueGetter: ({ row }) => resumo(row),
                },
              ]}
            />
            {detalhe && (
              <Card sx={{ mt: 2, p: 2 }}>
                <Typography variant="subtitle2">
                  {new Date(detalhe.createdAt).toLocaleString("pt-BR")} ·{" "}
                  {detalhe.user?.name} ·{" "}
                  {AREAS_LOG[detalhe.area] || detalhe.area} ·{" "}
                  {ACOES_LOG[detalhe.action] || detalhe.action}
                </Typography>
                {detalhe.registro && (
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                  >
                    Registro: {detalhe.registro}{" "}
                    {detalhe.rotulo ? `(${detalhe.rotulo})` : ""}
                  </Typography>
                )}
                {alteracoesDoLog(detalhe).map((a) => (
                  <Typography key={a.campo} variant="body2">
                    {rotuloCampo(a.campo)}:{" "}
                    {detalhe.action === "edita"
                      ? `${valorLog(a.campo, a.de)} → ${valorLog(
                          a.campo,
                          a.para
                        )}`
                      : valorLog(a.campo, a.para ?? a.de)}
                  </Typography>
                ))}
                {!detalhe.alteracoes?.length && detalhe.changedData && (
                  <Box
                    component="pre"
                    sx={{ fontSize: 12, maxHeight: 300, overflow: "auto" }}
                  >
                    {JSON.stringify(detalhe.changedData, null, 2)}
                  </Box>
                )}
              </Card>
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
